import React, { useEffect, useMemo, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { useParams, Link } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL;
/**
 * OmittedLetterPage
 *
 * A focused page for generating an "Omitted Creditor" letter packet.
 * Features
 *  1) Address + contact form for the creditor
 *  2) Optional free-text paragraph to insert into the letter body
 *  3) Calls backend to merge data into the fillable PDF template
 *  4) Optionally creates a #10 envelope PDF
 *  5) Optionally auto-attaches Notice of Bankruptcy Case and/or Discharge by pulling from the docket
 *  6) Saves the full packet in the database and optionally in the client's OneDrive /letters folder
 *  7) Creates an independently completable service-document todo
 *
 * Assumed endpoints (implement on Django backend):
 *  - GET  /api/case/:caseId/   -> { id, case_number, debtor_names, one_drive_folder_url? }
 *  - POST /api/case/:caseId/letters/omitted-creditor/generate
 *      Body: {
 *        creditor: { name, attention, address1, address2, city, state, zip, email, phone },
 *        extra_text, // string | null
 *        create_envelope: boolean,
 *        attach_notice_of_bankruptcy_case: boolean,
 *        attach_discharge: boolean,
 *        save_to_onedrive: boolean
 *      }
 *      Returns: {
 *        letter_pdf_url,              // URL to the merged letter PDF
 *        envelope_pdf_url?,           // URL to the created envelope PDF
 *        packet_pdf_url?,             // URL to a combined packet PDF (letter + attachments)
 *        onedrive_folder_url?,        // Where it was saved
 *        attached_docs: [
 *          { kind: 'notice_of_bankruptcy_case'|'discharge'|'other', title, pdf_url }
 *        ]
 *      }
 */

const initialCreditor = {
  name: '',
  attention: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  phone: '',
};

export default function OmittedLetterPage() {
  const { caseId } = useParams();
  const [loadingCase, setLoadingCase] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [creditor, setCreditor] = useState(initialCreditor);
  const [extraText, setExtraText] = useState('');
  const [createEnvelope, setCreateEnvelope] = useState(true);
  const [attachNOBC, setAttachNOBC] = useState(true); // Notice of Bankruptcy Case
  const [attachDischarge, setAttachDischarge] = useState(false);
  const [saveToOneDrive, setSaveToOneDrive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [savedPackets, setSavedPackets] = useState([]);
  const [loadingPackets, setLoadingPackets] = useState(true);
  const [packetError, setPacketError] = useState('');
  const [downloadingPacketId, setDownloadingPacketId] = useState(null);
  const [deletingPacketId, setDeletingPacketId] = useState(null);

const [ccSearch, setCcSearch] = useState('');
const [ccSuggestions, setCcSuggestions] = useState([]);
const [ccLoading, setCcLoading] = useState(false);
const [ccError, setCcError] = useState(null);
const [ccModalOpen, setCcModalOpen] = useState(false);
  // Docket findings
  const [loadingDocket, setLoadingDocket] = useState(true);
  const [docket, setDocket] = useState({ notice_of_bankruptcy_case: null, discharge: null });

const token = useMemo(() => {
  return localStorage.getItem('token') || localStorage.getItem('accessToken') || '';
}, []);

async function fetchCommonCreditorSuggestions(value) {
  const q = (value || '').trim();
  if (!q || q.length < 2) {
    setCcSuggestions([]);
    return;
  }
  setCcLoading(true);
  setCcError(null);
  try {
    const res = await fetch(`${API_URL}/common-creditors/?q=${encodeURIComponent(q)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error('Failed to load common creditors');
    }
    const json = await res.json();
    const items = Array.isArray(json) ? json : json.results || [];
    setCcSuggestions(items);
  } catch (e) {
    console.error(e);
    setCcError(e.message || 'Failed to load common creditors');
    setCcSuggestions([]);
  } finally {
    setCcLoading(false);
  }
}

const onCcSuggestionsFetchRequested = ({ value }) => {
  fetchCommonCreditorSuggestions(value);
};

const onCcSuggestionsClearRequested = () => {
  setCcSuggestions([]);
};

const getCcSuggestionValue = (s) => s.account_name || s.name || '';

const renderCcSuggestion = (s) => (
  <li className="px-4 py-2 hover:bg-indigo-50 cursor-pointer">
    <strong>{s.account_name || s.name}</strong>
    <div className="text-xs text-gray-500 truncate">
      {[s.street1, s.street2].filter(Boolean).join(', ')}
    </div>
    <div className="text-xs text-gray-400">
      {s.city}, {s.state}
    </div>
  </li>
);

const onCcSuggestionSelected = (event, { suggestion }) => {
  setCreditor((prev) => ({
    ...prev,
    name: suggestion.account_name || suggestion.name || prev.name,
    address1: suggestion.street1 || prev.address1,
    address2: suggestion.street2 || prev.address2,
    city: suggestion.city || prev.city,
    state: suggestion.state || prev.state,
    zip: suggestion.zip_code || suggestion.zip || prev.zip,
    email: suggestion.email || prev.email,
    phone: suggestion.phone || prev.phone,
  }));
  // Close modal and reset search after selection
  setCcModalOpen(false);
  setCcSearch('');
  setCcSuggestions([]);
};

const ccInputProps = {
  placeholder: 'Search common creditors…',
  value: ccSearch,
  onChange: (_e, { newValue }) => setCcSearch(newValue),
};

  const autosuggestTheme = {
    container: 'relative w-full',
    containerOpen: 'relative w-full',
    input:
      'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
    inputOpen:
      'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
    inputFocused:
      'mt-1 block w-full rounded-md border border-indigo-500 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500',
    suggestionsContainer:
      'mt-1 w-full border border-gray-200 rounded-md bg-white shadow-lg max-h-64 overflow-y-auto',
    suggestionsContainerOpen:
      'mt-1 w-full border border-gray-200 rounded-md bg-white shadow-lg max-h-64 overflow-y-auto',
    suggestionsList: 'divide-y divide-gray-100',
    suggestion: 'px-4 py-2 cursor-pointer text-sm text-gray-800',
    suggestionHighlighted: 'px-4 py-2 cursor-pointer text-sm bg-indigo-50 text-gray-900',
  };

  useEffect(() => {
    async function fetchCase() {
      setLoadingCase(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/case/${caseId}/overview`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`Failed to load case ${caseId}`);
        const json = await res.json();
        setCaseData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingCase(false);
      }
    }
    if (caseId) fetchCase();

    async function fetchDocket() {
      setLoadingDocket(true);
      try {
        const res = await fetch(`${API_URL}/case/${caseId}/dockets/notices/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to load docket notices');
        const json = await res.json();
        setDocket({
          notice_of_bankruptcy_case: json.notice_of_bankruptcy_case || null,
          discharge: json.discharge || null,
        });
        // If we don't have a NOBC, uncheck by default
        if (!json.notice_of_bankruptcy_case) setAttachNOBC(false);
      } catch (e) {
        // keep defaults if it fails, but surface a non-blocking error in console
        console.warn(e);
      } finally {
        setLoadingDocket(false);
      }
    }
    if (caseId) fetchDocket();

    async function fetchSavedPackets() {
      setLoadingPackets(true);
      setPacketError('');
      try {
        const res = await fetch(
          `${API_URL}/case/${caseId}/letters/omitted-creditor/packets/`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'include',
          },
        );
        if (!res.ok) throw new Error('Failed to load saved omitted-creditor packets');
        const json = await res.json();
        setSavedPackets(Array.isArray(json.packets) ? json.packets : []);
      } catch (e) {
        setPacketError(e.message || 'Failed to load saved packets');
      } finally {
        setLoadingPackets(false);
      }
    }
    if (caseId) fetchSavedPackets();
  }, [caseId, token]);

  function handleCreditorChange(e) {
    const { name, value } = e.target;
    setCreditor((prev) => ({ ...prev, [name]: value }));
  }

  function validateForm() {
    if (!creditor.name) return 'Creditor name is required';
    if (!creditor.address1) return 'Address line 1 is required';
    if (!creditor.city) return 'City is required';
    if (!creditor.state || creditor.state.length !== 2) return 'State (2-letter) is required';
    if (!creditor.zip) return 'ZIP code is required';
    return null;
  }

  async function handleGenerate() {
    const v = validateForm();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    setResult(null);
    setError(null);
    try {
      const payload = {
        creditor,
        extra_text: extraText?.trim() || null,
        create_envelope: !!createEnvelope,
        attach_notice_of_bankruptcy_case: !!attachNOBC,
        attach_discharge: !!attachDischarge,
        save_to_onedrive: !!saveToOneDrive,
      };
      const res = await fetch(`${API_URL}/case/${caseId}/letters/omitted-creditor/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to generate letter');
      }
      const json = await res.json();
      setResult(json);
      if (json.packet) {
        setSavedPackets((current) => [
          json.packet,
          ...current.filter((packet) => packet.id !== json.packet.id),
        ]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function downloadSavedPacket(packet) {
    setDownloadingPacketId(packet.id);
    setPacketError('');
    try {
      const res = await fetch(`${API_URL}${packet.download_url}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to download saved packet');
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = packet.filename || 'omitted-creditor-service-packet.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setPacketError(e.message || 'Failed to download saved packet');
    } finally {
      setDownloadingPacketId(null);
    }
  }

  async function deleteSavedPacket(packet) {
    const confirmed = window.confirm(
      `Delete the saved packet for ${packet.creditor_name}? Its service todo will also be deleted.`,
    );
    if (!confirmed) return;
    setDeletingPacketId(packet.id);
    setPacketError('');
    try {
      const res = await fetch(`${API_URL}${packet.download_url}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete saved packet');
      setSavedPackets((current) => current.filter((item) => item.id !== packet.id));
      if (result?.packet?.id === packet.id) setResult(null);
    } catch (e) {
      setPacketError(e.message || 'Failed to delete saved packet');
    } finally {
      setDeletingPacketId(null);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 w-full flex justify-center">
      <div className="w-full max-w-3xl">
        <header className="mt-8 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Omitted Creditor Letter</h1>
          <p className="text-sm text-gray-600 mt-1">
            Generate a formal notice letter to a creditor omitted from the original schedules.
          </p>
        </header>

        {/* Case summary */}
        <section className="mb-6">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            {loadingCase ? (
              <p className="text-gray-600">Loading case…</p>
            ) : error && !result ? (
              <p className="text-red-600">{error}</p>
            ) : (
              <div className="text-sm text-gray-700 space-y-1">
                <div className="flex flex-wrap gap-4">
                  <div>
                    <span className="font-medium">Case ID:</span> {caseId}
                  </div>
                  {caseData?.case_number ? (
                    <div>
                      <span className="font-medium">Case #:</span> {caseData.case_number}
                    </div>
                  ) : null}
                  {caseData?.debtor_names ? (
                    <div>
                      <span className="font-medium">Debtor(s):</span> {caseData.debtor_names}
                    </div>
                  ) : null}
                </div>
                {caseData?.one_drive_folder_url ? (
                  <div>
                    <a href={caseData.one_drive_folder_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                      Open OneDrive case folder
                    </a>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </section>

        <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Saved Service Packets</h2>
              <p className="mt-1 text-sm text-gray-600">
                Each saved packet has its own service-document todo.
              </p>
            </div>
            {loadingPackets && <span className="text-sm text-gray-500">Loading…</span>}
          </div>

          {!loadingPackets && savedPackets.length === 0 && (
            <p className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-600">
              No omitted-creditor packets have been saved for this case yet.
            </p>
          )}

          {savedPackets.length > 0 && (
            <div className="mt-4 space-y-3">
              {savedPackets.map((packet) => (
                <div
                  key={packet.id}
                  className="rounded-md border border-gray-200 bg-gray-50 p-3"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 text-sm">
                      <div className="font-medium text-gray-900">{packet.creditor_name}</div>
                      {packet.creditor_address && (
                        <div className="mt-0.5 text-gray-600">{packet.creditor_address}</div>
                      )}
                      <div className="mt-1 text-xs text-gray-500">
                        Created {new Date(packet.created_at).toLocaleString()} ·{' '}
                        <span className={packet.served_at ? 'text-green-700' : 'text-amber-700'}>
                          {packet.served_at ? 'Service completed' : 'Awaiting service'}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => downloadSavedPacket(packet)}
                        disabled={downloadingPacketId === packet.id}
                        className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300"
                      >
                        {downloadingPacketId === packet.id ? 'Downloading…' : 'Download Packet'}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteSavedPacket(packet)}
                        disabled={deletingPacketId === packet.id}
                        className="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        {deletingPacketId === packet.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {packetError && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {packetError}
            </div>
          )}
        </section>

        {/* Form grid */}
        <section className="grid grid-cols-1 gap-6">
          {/* Creditor address card */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Creditor Contact</h2>
              <button
                type="button"
                onClick={() => setCcModalOpen(true)}
                className="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
              >
                Search Common Creditors
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Creditor Name *</label>
                <input
                  type="text"
                  name="name"
                  value={creditor.name}
                  onChange={handleCreditorChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Acme Bank, N.A."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Attention / Department</label>
                <input
                  type="text"
                  name="attention"
                  value={creditor.attention}
                  onChange={handleCreditorChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Bankruptcy Department"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={creditor.email}
                  onChange={handleCreditorChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="bk@acmebank.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Address Line 1 *</label>
                <input
                  type="text"
                  name="address1"
                  value={creditor.address1}
                  onChange={handleCreditorChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="123 Main St"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                <input
                  type="text"
                  name="address2"
                  value={creditor.address2}
                  onChange={handleCreditorChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Suite 400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">City *</label>
                <input
                  type="text"
                  name="city"
                  value={creditor.city}
                  onChange={handleCreditorChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">State (2‑letter) *</label>
                <input
                  type="text"
                  name="state"
                  maxLength={2}
                  value={creditor.state}
                  onChange={(e) => handleCreditorChange({ target: { name: 'state', value: e.target.value.toUpperCase() } })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 uppercase"
                  placeholder="TN"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">ZIP *</label>
                <input
                  type="text"
                  name="zip"
                  value={creditor.zip}
                  onChange={handleCreditorChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="37902"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={creditor.phone}
                  onChange={handleCreditorChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Extra text card */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-medium text-gray-900">Optional Extra Paragraph</h2>
              <span className="text-xs text-gray-500">This will be inserted into the body of the letter.</span>
            </div>
            <textarea
              value={extraText}
              onChange={(e) => setExtraText(e.target.value)}
              rows={5}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="e.g., Please update your records to reflect Debtor's bankruptcy filing. If you have any questions, contact our office."
            />
          </div>

          {/* Options card */}
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded" checked={createEnvelope} onChange={(e) => setCreateEnvelope(e.target.checked)} />
                Create #10 envelope PDF
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded" checked={saveToOneDrive} onChange={(e) => setSaveToOneDrive(e.target.checked)} />
                Save packet to OneDrive /letters
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={attachNOBC}
                  disabled={loadingDocket || !docket.notice_of_bankruptcy_case}
                  onChange={(e) => setAttachNOBC(e.target.checked)}
                />
                <span>
                  Attach Notice of Bankruptcy Case (from docket)
                  {loadingDocket ? (
                    <span className="ml-2 text-xs text-gray-500">checking…</span>
                  ) : docket.notice_of_bankruptcy_case ? (
                    <span className="ml-2 text-xs text-green-600">found</span>
                  ) : (
                    <span className="ml-2 text-xs text-amber-600">not found</span>
                  )}
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={attachDischarge}
                  disabled={loadingDocket || !docket.discharge}
                  onChange={(e) => setAttachDischarge(e.target.checked)}
                />
                <span>
                  Attach Discharge (from docket)
                  {loadingDocket ? (
                    <span className="ml-2 text-xs text-gray-500">checking…</span>
                  ) : docket.discharge ? (
                    <span className="ml-2 text-xs text-green-600">found</span>
                  ) : (
                    <span className="ml-2 text-xs text-amber-600">not found</span>
                  )}
                </span>
              </label>
            </div>
          </div>

          {/* Docket previews */}
          {!loadingDocket && (docket.notice_of_bankruptcy_case || docket.discharge) && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Docket documents detected</h3>
              <ul className="list-disc pl-5 text-sm">
                {docket.notice_of_bankruptcy_case && (
                  <li>
                    <a className="text-indigo-600 hover:underline" href={docket.notice_of_bankruptcy_case.pdf_url} target="_blank" rel="noreferrer">
                      {docket.notice_of_bankruptcy_case.title || 'Notice of Bankruptcy Case'}
                    </a>
                  </li>
                )}
                {docket.discharge && (
                  <li>
                    <a className="text-indigo-600 hover:underline" href={docket.discharge.pdf_url} target="_blank" rel="noreferrer">
                      {docket.discharge.title || 'Discharge'}
                    </a>
                  </li>
                )}
              </ul>
            </div>
          )}

          {ccModalOpen && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900">Search Common Creditors</h3>
                  <button
                    type="button"
                    onClick={() => setCcModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <div className="px-4 py-4 space-y-3">
                  <Autosuggest
                    suggestions={ccSuggestions}
                    onSuggestionsFetchRequested={onCcSuggestionsFetchRequested}
                    onSuggestionsClearRequested={onCcSuggestionsClearRequested}
                    getSuggestionValue={getCcSuggestionValue}
                    renderSuggestion={renderCcSuggestion}
                    onSuggestionSelected={onCcSuggestionSelected}
                    inputProps={ccInputProps}
                    highlightFirstSuggestion
                    theme={autosuggestTheme}
                  />
                  {ccLoading && (
                    <p className="mt-1 text-xs text-gray-500">Loading suggestions…</p>
                  )}
                  {ccError && (
                    <p className="mt-1 text-xs text-red-500">{ccError}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Start typing the creditor name or address, then select a match to auto-fill the form.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">All generated files will also appear in the case timeline once saved.</div>
            <button
              onClick={handleGenerate}
              disabled={submitting || loadingCase}
              className={`inline-flex items-center px-4 py-2 rounded-md text-white ${submitting || loadingCase ? 'bg-indigo-300' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            >
              {submitting ? 'Generating…' : 'Generate Packet'}
            </button>
          </div>

          {/* Results */}
          {result && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-lg font-medium text-gray-900 mb-3">Downloads</h2>
              <div className="mb-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                The packet was saved and its service-document todo was created.
              </div>
              <ul className="list-disc pl-5 space-y-2 text-indigo-700">
                {result.packet && (
                  <li>
                    <button
                      type="button"
                      onClick={() => downloadSavedPacket(result.packet)}
                      disabled={downloadingPacketId === result.packet.id}
                      className="hover:underline disabled:text-indigo-300"
                    >
                      {downloadingPacketId === result.packet.id
                        ? 'Downloading Saved Service Packet…'
                        : 'Saved Service Packet (ZIP)'}
                    </button>
                  </li>
                )}
                {result.packet_pdf_url && (
                  <li>
                    <a href={result.packet_pdf_url} target="_blank" rel="noreferrer" className="hover:underline">Combined Packet PDF</a>
                  </li>
                )}
                {result.letter_pdf_url && (
                  <li>
                    <a href={result.letter_pdf_url} target="_blank" rel="noreferrer" className="hover:underline">Letter PDF</a>
                  </li>
                )}
                {result.envelope_pdf_url && (
                  <li>
                    <a href={result.envelope_pdf_url} target="_blank" rel="noreferrer" className="hover:underline">Envelope PDF</a>
                  </li>
                )}
                {Array.isArray(result.attached_docs) && result.attached_docs.length > 0 && (
                  <li className="mt-2">
                    <div className="text-gray-900 font-medium">Attachments</div>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      {result.attached_docs.map((d, idx) => (
                        <li key={idx}>
                          <a href={d.pdf_url} target="_blank" rel="noreferrer" className="hover:underline">{d.title || d.kind}</a>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>

              {result.onedrive_folder_url && (
                <div className="mt-3">
                  <a href={result.onedrive_folder_url} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline">
                    Open OneDrive /letters folder
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Error bar */}
          {error && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
        </section>

        {/* Footer nav */}
        <div className="mt-8 mb-20 flex items-center justify-between text-sm">
          <Link to={`${API_URL}/case/${caseId}`} className="text-gray-600 hover:text-gray-900 hover:underline">← Back to Case Home</Link>
        </div>
      </div>
    </div>
  );
}
